import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChangeOrderList } from '../../components/changes/ChangeOrderList';
import { ChangeOrderForm } from '../../components/changes/ChangeOrderForm';
import { ChangeOrderDetail } from '../../components/changes/ChangeOrderDetail';
import {
  changeManagementService,
  ChangeOrderFull,
  ChangeStats,
} from '../../services/change-management.service';
import { useAuth } from '../../contexts/AuthContext';

export function ChangeOrdersPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orders, setOrders] = useState<ChangeOrderFull[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<ChangeOrderFull | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState<ChangeStats | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'client_approved' | 'approved' | 'rejected' | 'executed'>('all');
  const [signatureModal, setSignatureModal] = useState<ChangeOrderFull | null>(null);

  useEffect(() => {
    if (projectId) {
      loadData();
    }
  }, [projectId]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError('');
      const [ordersData, statsData] = await Promise.all([
        changeManagementService.getChangeOrders(projectId!),
        changeManagementService.getChangeStats(projectId!)
      ]);
      setOrders(ordersData);
      setStats(statsData);
    } catch (err: any) {
      setError(err.message || 'Error al cargar datos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async (data: any) => {
    try {
      await changeManagementService.createChangeOrder({
        ...data,
        projectId: projectId!
      });
      setShowForm(false);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Error al crear orden de cambio');
    }
  };

  const handleApprove = async (id: string) => {
    try {
      await changeManagementService.approveChangeOrder(id);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Error al aprobar orden');
    }
  };

  const handleReject = async (id: string, reason: string) => {
    try {
      await changeManagementService.rejectChangeOrder(id, reason);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Error al rechazar orden');
    }
  };

  const handleExecute = async (id: string) => {
    try {
      await changeManagementService.executeChangeOrder(id);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Error al ejecutar orden');
    }
  };

  const filteredOrders = orders.filter(order => {
    if (filter === 'all') return true;
    return order.status === filter;
  });

  const canApprove = user?.role === 'project_lead' || user?.role === 'general_manager' || user?.role === 'sys_admin';
  const canExecute = user?.role === 'project_lead' || user?.role === 'general_manager' || user?.role === 'sys_admin';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando órdenes de cambio...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Órdenes de Cambio</h1>
          <p className="text-gray-600">Gestiona las modificaciones y cambios en obra</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
        >
          + Nueva Orden de Cambio
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 text-red-600 rounded-md border border-red-200">
          {error}
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm text-gray-500">Impacto Total</div>
            <div className={`text-2xl font-bold ${stats.totalImpact >= 0 ? 'text-primary' : 'text-green-600'}`}>
              ${stats.totalImpact.toFixed(2)}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm text-gray-500">Pendientes</div>
            <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm text-gray-500">Aprobados cliente</div>
            <div className="text-2xl font-bold text-green-600">{stats.clientApproved}</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm text-gray-500">Ejecutados</div>
            <div className="text-2xl font-bold text-blue-600">{stats.executed}</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm text-gray-500">Total Órdenes</div>
            <div className="text-2xl font-bold text-gray-800">{stats.total}</div>
          </div>
        </div>
      )}

      <div className="flex gap-2 overflow-x-auto pb-2">
        {(['all', 'pending', 'client_approved', 'approved', 'rejected', 'executed'] as const).map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              filter === status
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {status === 'all' ? 'Todos' : status === 'client_approved' ? 'Aprobados cliente' : status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      <ChangeOrderList
        orders={filteredOrders}
        onSelect={setSelectedOrder}
        onApprove={canApprove ? handleApprove : undefined}
        onReject={canApprove ? handleReject : undefined}
        onExecute={canExecute ? handleExecute : undefined}
        onViewSignature={(order) => setSignatureModal(order)}
      />

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold">Nueva Orden de Cambio</h3>
                <button
                  onClick={() => setShowForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              <ChangeOrderForm
                projectId={projectId!}
                projectBudget={0}
                onSubmit={handleCreate}
                onCancel={() => setShowForm(false)}
              />
            </div>
          </div>
        </div>
      )}

      {selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold">Detalle de Orden de Cambio</h3>
                <button
                  onClick={() => setSelectedOrder(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              <ChangeOrderDetail
                order={selectedOrder as any}
                onApprove={canApprove ? handleApprove : undefined}
                onReject={canApprove ? handleReject : undefined}
                onExecute={canExecute ? handleExecute : undefined}
                onClose={() => setSelectedOrder(null)}
              />
            </div>
          </div>
        </div>
      )}

      {signatureModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
          onClick={() => setSignatureModal(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">✍️ Firma del cliente</h3>
              <button
                onClick={() => setSignatureModal(null)}
                className="text-gray-400 hover:text-gray-600 text-xl"
              >
                ✕
              </button>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <p className="text-xs text-green-700 font-medium mb-1">Firmado por</p>
              <p className="font-bold text-green-900">
                {signatureModal.clientSignatureName || 'N/A'}
              </p>
              {signatureModal.clientApprovedAt && (
                <p className="text-xs text-green-700 mt-1">
                  {new Date(signatureModal.clientApprovedAt).toLocaleString('es-MX')}
                </p>
              )}
            </div>

            <div className="bg-slate-50 rounded-lg p-4 mb-4">
              <p className="text-xs text-slate-500 mb-2">Orden</p>
              <p className="font-mono text-sm text-slate-700">
                {signatureModal.number}
              </p>
              <p className="font-semibold text-slate-900 mt-1">
                {signatureModal.title || 'Cambio sin título'}
              </p>
              <p className="text-sm text-slate-600 mt-1">
                Impacto: ${(signatureModal.impact?.impact || 0).toFixed(2)}
              </p>
            </div>

            {signatureModal.clientSignatureSvg && (
              <div>
                <p className="text-xs text-slate-500 mb-2">Firma registrada</p>
                <img
                  src={`data:image/svg+xml;utf8,${encodeURIComponent(signatureModal.clientSignatureSvg)}`}
                  alt="Firma del cliente"
                  className="bg-white rounded border border-slate-200 w-full max-h-[200px] object-contain"
                />
              </div>
            )}

            <div className="flex justify-end mt-4 pt-4 border-t border-slate-200">
              <button
                onClick={() => setSignatureModal(null)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-medium transition"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}